import styled from 'styled-components';

import { fieldStateCss } from './fieldStateStyles';

/**
 * The app's slider parts, shared by the dub run's Voice Settings
 * (`aiVoices/DubDirectionField`, where they were born, PLAT-12023) and the
 * side panel's slider fields (`common/PanelSliderField`, PLAT-12137). One
 * hairline slider, one quiet value field, so two surfaces can't draw two.
 * See docs/design-system.md § "Slider With Ends" and § "Panel Fields".
 */

// A hairline track with the gold fill up to the thumb, as the app's other
// sliders draw it; the keyboard focus ring is the neutral one.
export const RangeSlider = styled.input<{ $fill: number }>`
  flex: 1 1 auto;
  min-width: 0;
  height: 2px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    var(--accent) 0%,
    var(--accent) ${p => p.$fill}%,
    var(--border) ${p => p.$fill}%,
    var(--border) 100%
  );
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
    transition: transform var(--transition-fast);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: none;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
    transition: transform var(--transition-fast);
  }

  &:hover:not(:disabled)::-webkit-slider-thumb,
  &:active:not(:disabled)::-webkit-slider-thumb {
    transform: scale(1.15);
  }

  &:hover:not(:disabled)::-moz-range-thumb,
  &:active:not(:disabled)::-moz-range-thumb {
    transform: scale(1.15);
  }

  &:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }

  &:focus-visible::-moz-range-thumb {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// The value, demoted: a quiet number beside the label that is still a field.
// Borderless and muted at rest so it reads as "0.54" rather than as a second
// control; the standard hover → focus escalation once a pointer or the
// keyboard reaches it. A value the models would refuse keeps the destructive
// border at every state instead (PLAT-11797).
export const QuietValueInput = styled.input<{ $invalid: boolean }>`
  flex: 0 0 52px;
  width: 52px;
  height: 24px;
  padding: 0 6px;
  box-sizing: border-box;
  text-align: right;
  background: transparent;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
  ${fieldStateCss}
  border-color: ${p => (p.$invalid ? 'var(--destructive)' : 'transparent')};

  &:hover:not(:disabled) {
    color: var(--foreground);
    border-color: ${p =>
      p.$invalid ? 'var(--destructive)' : 'var(--text-muted)'};
  }

  &:focus {
    color: var(--foreground);
    border-color: ${p =>
      p.$invalid ? 'var(--destructive)' : 'var(--focus-border)'};
    box-shadow: 0 0 0 1px
      ${p => (p.$invalid ? 'var(--destructive)' : 'var(--focus-ring)')};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;
